import { agentById, type AgentId } from "./nav.js";

export type ActivityKind =
	| "status"
	| "thinking"
	| "tool"
	| "gate"
	| "outcome";

export type ActivityItem = {
	id: string;
	agentId: AgentId;
	kind: ActivityKind;
	title: string;
	detail: string;
	at: string;
	/** pending | running | waiting | done | denied */
	state: "pending" | "running" | "waiting" | "done" | "denied";
};

export type AgentLiveStatus = {
	agentId: AgentId;
	label: string;
	state: "idle" | "running" | "waiting" | "locked";
	summary: string;
};

/**
 * Demo activity until DOs emit structured run steps and Matrix reads audit_log.
 * Shape matches what a future `/api/agents/:id/activity` should return.
 */
export function liveStatusFor(
	agentId: AgentId,
	setupDone: boolean,
	onboardingStep?: string,
): AgentLiveStatus {
	const agent = agentById(agentId);

	if (!setupDone && agentId !== "onboarding") {
		return {
			agentId,
			label: "Locked",
			state: "locked",
			summary: "Finish onboarding to unlock this agent.",
		};
	}

	if (agent.availability === "soon") {
		return {
			agentId,
			label: "Coming soon",
			state: "locked",
			summary: `${agent.name} is coming soon — chat is a preview for now.`,
		};
	}

	switch (agentId) {
		case "onboarding": {
			if (setupDone) {
				return {
					agentId,
					label: "Ready when you are",
					state: "idle",
					summary: "Ask in chat — trail and reasoning show up here.",
				};
			}
			if (onboardingStep === "researching") {
				return {
					agentId,
					label: "Learning the brand",
					state: "running",
					summary: "Reading the public site to see how we can help.",
				};
			}
			if (onboardingStep === "findings") {
				return {
					agentId,
					label: "Waiting on you",
					state: "waiting",
					summary: "Findings are ready — confirm if it feels right.",
				};
			}
			return {
				agentId,
				label: "Guiding setup",
				state: "running",
				summary: "Collecting profile and company details in chat.",
			};
		}
		case "paid_media":
			return {
				agentId,
				label: "Ready when you are",
				state: "idle",
				summary: "Connect Google Ads, then brief a campaign in chat.",
			};
		case "ga4_anomaly":
			return {
				agentId,
				label: "Ready when you are",
				state: "idle",
				summary: "Bind GA4 to start scheduled anomaly checks.",
			};
		case "seo":
			return {
				agentId,
				label: "Ready when you are",
				state: "idle",
				summary: "Connect Search Console to scan query performance.",
			};
		case "reporting":
			return {
				agentId,
				label: "Ready when you are",
				state: "idle",
				summary: "Connect GA4 or Ads for a cross-channel digest.",
			};
		case "developer":
			return {
				agentId,
				label: "Ready when you are",
				state: "idle",
				summary:
					"Connect GitHub, Vercel, Cloudflare, or GoDaddy for a Sites readiness check.",
			};
		case "content":
		case "social":
		case "email":
		case "creative":
		case "cro":
		case "local":
			return {
				agentId,
				label: "Coming soon",
				state: "locked",
				summary: `${agent.name} is coming soon.`,
			};
		default: {
			const _exhaustive: never = agentId;
			return _exhaustive;
		}
	}
}

/**
 * Trail items for the current / last run. Empty while idle so the rail
 * can show an empty state instead of stale stubs.
 */
export function activityHistoryFor(
	agentId: AgentId,
	liveState: AgentLiveStatus["state"],
): ActivityItem[] {
	if (liveState === "idle" || liveState === "locked") {
		return [];
	}

	const now = Date.now();
	const ago = (ms: number) => new Date(now - ms).toISOString();

	if (agentId === "onboarding") {
		return [
			{
				id: "ob-1",
				agentId,
				kind: "thinking",
				title: "Planning setup path",
				detail: "Name → email → company → site → research pack.",
				at: ago(120_000),
				state: "done",
			},
			{
				id: "ob-2",
				agentId,
				kind: "tool",
				title: "Site research",
				detail: "Reviewing public pages on your site.",
				at: ago(60_000),
				state: liveState === "running" ? "running" : "done",
			},
			{
				id: "ob-3",
				agentId,
				kind: "outcome",
				title: "Company pack drafted",
				detail: "Brand voice + site summary ready for review.",
				at: ago(30_000),
				state: liveState === "waiting" ? "waiting" : "done",
			},
		];
	}

	return [];
}
